import {TreeNode} from 'primeng/api';
import {
    ECpmCObjectStoreEntryAttributesProxyApiEnum,
    ECpmCObjectStoreEntryProxyTypeApiEnum
} from '../../../gen/cpm-c-api-ts/src/enums/c-customer/cpmc-object-store-entry-proxy.api-enums';
import {
    ECpmCTaskAttributes1ApiEnum,
    ECpmCTaskStatusApiEnum,
    ECpmCTaskTypeApiEnum
} from '../../../gen/cpm-c-api-ts/src/enums/c-customer/cpmc-task.api-enums';
import {ECpmCReferenceExpressionSourceTypeApiEnum} from '../../../gen/cpm-c-api-ts/src/enums/c-global/cpmc-reference-expression.api-enums';
import {CpmCTaskApiModel} from '../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-task.api-model';
import {TableTagDef} from '../../../sdk/src/components/generic-table/table-col-def';
import {TreeTagDef} from '../../../sdk/src/components/generic-tree/tree-col-def';
import {StringUtils} from '../../../sdk/src/utils/string.util';
import {TreeUtils} from '../../../sdk/src/utils/tree.utils';
import {TaskTreeViewExtension} from '../api-extensions/views/task-tree.view-extension';
import {ObjectStoreEntriesTransientCache} from '../transient-cache/object-store-entries.transient-cache';

/**
 * Task utilities
 */
export abstract class TaskUtils {
    public static readonly ICON_REQUIRE_MANUAL_INPUT = 'pi pi-list';

    public static readonly statusDescriptions: Record<string, string> = {
        ['CREATED']: 'Task was created',
        ['READY']: 'Task is ready to be executed',
        ['IN_PROGRESS']: 'Task is currently being executed',
        ['SUCCESS']: 'Task completed successfully',
        ['FAILED']: 'Task failed'
    };

    public static readonly statusTags: (TableTagDef | TreeTagDef)[] = [
        {
            style: {background: '#eee', color: 'black !important'},
            label: StringUtils.toHumanFormatedStr(ECpmCTaskStatusApiEnum.CREATED),
            value: ECpmCTaskStatusApiEnum.CREATED,
            tooltip: TaskUtils.statusDescriptions[ECpmCTaskStatusApiEnum.CREATED],
            icon: 'pi pi-circle-fill',
            iconStyle: {color: '#999', fontSize: '70%', verticalAlign: 'middle'}
        },
        {
            style: {background: '#B5E8F7', color: 'black !important'},
            label: StringUtils.toHumanFormatedStr(ECpmCTaskStatusApiEnum.READY),
            value: ECpmCTaskStatusApiEnum.READY,
            tooltip: TaskUtils.statusDescriptions[ECpmCTaskStatusApiEnum.READY],
            icon: 'pi pi-circle-fill',
            iconStyle: {color: '#4BB4E6', fontSize: '70%', verticalAlign: 'middle'}
        },
        {
            style: {background: '#fff8da', color: 'black !important'},
            label: StringUtils.toHumanFormatedStr(ECpmCTaskStatusApiEnum.IN_PROGRESS),
            value: ECpmCTaskStatusApiEnum.IN_PROGRESS,
            tooltip: TaskUtils.statusDescriptions[ECpmCTaskStatusApiEnum.IN_PROGRESS],
            icon: 'pi pi-circle-fill',
            iconStyle: {color: '#ffd200', fontSize: '70%', verticalAlign: 'middle'}
        },
        {
            style: {background: '#ECFDEF', color: 'black !important'},
            label: StringUtils.toHumanFormatedStr(ECpmCTaskStatusApiEnum.SUCCESS),
            value: ECpmCTaskStatusApiEnum.SUCCESS,
            tooltip: TaskUtils.statusDescriptions[ECpmCTaskStatusApiEnum.SUCCESS],
            icon: 'pi pi-circle-fill',
            iconStyle: {color: '#32c832', fontSize: '70%', verticalAlign: 'middle'}
        },
        {
            style: {background: '#FDE5E6', color: 'black !important'},
            label: StringUtils.toHumanFormatedStr(ECpmCTaskStatusApiEnum.FAILED),
            value: ECpmCTaskStatusApiEnum.FAILED,
            tooltip: TaskUtils.statusDescriptions[ECpmCTaskStatusApiEnum.FAILED],
            icon: 'pi pi-circle-fill',
            iconStyle: {color: '#cd3c14', fontSize: '70%', verticalAlign: 'middle'}
        }
    ];

    public static readonly waitingForInputTag: TableTagDef | TreeTagDef = {
        style: {background: '#fff8da', color: 'black !important'},
        label: 'Waiting for input',
        value: 'WAITING_FOR_INPUT',
        tooltip: 'Task is waiting for manual input',
        icon: 'pi pi-circle-fill',
        iconStyle: {color: '#ffd200', fontSize: '70%', verticalAlign: 'middle'},
        styleClass: undefined
    };
    public static readonly allowedStatusChanges: (TableTagDef | TreeTagDef)[] = TaskUtils.statusTags.filter(
        (x) => x.value != ECpmCTaskStatusApiEnum.CREATED
    );

    public static readonly statusTagsByStatus: Record<ECpmCTaskStatusApiEnum, TableTagDef | TreeTagDef> =
        Object.fromEntries(TaskUtils.statusTags.map((x) => [x.value, x])) as Record<
            ECpmCTaskStatusApiEnum,
            TableTagDef | TreeTagDef
        >;

    public static readonly outdatedTag: TableTagDef | TreeTagDef = {
        style: {background: '#FCE7C8', color: 'black !important'},
        label: 'Outdated',
        value: 'OUTDATED',
        tooltip: 'Task result is outdated and must be executed again',
        icon: 'pi pi-history',
        iconStyle: {color: '#C97A00', fontSize: '70%', verticalAlign: 'middle'},
        styleClass: undefined
    };

    public static readonly outdatedFailedTag: TableTagDef | TreeTagDef = {
        style: {background: '#FDE5E6', color: 'black !important'},
        label: 'Outdated failed',
        value: 'OUTDATED_FAILED',
        tooltip: 'Task result is outdated and the last execution failed',
        icon: 'pi pi-history',
        iconStyle: {color: '#cd3c14', fontSize: '70%', verticalAlign: 'middle'},
        styleClass: undefined
    };

    public static isOutdated(
        task?: Pick<CpmCTaskApiModel, 'attributes1'> | Pick<TaskTreeViewExtension, 'attributes1'>
    ): boolean {
        if (!task) return false;
        return (task.attributes1 & ECpmCTaskAttributes1ApiEnum.OUTDATED) !== 0;
    }

    public static isOutdatedFailed(
        task?: Pick<CpmCTaskApiModel, 'attributes1'> | Pick<TaskTreeViewExtension, 'attributes1'>
    ): boolean {
        if (!task) return false;

        return (
            (task.attributes1 & ECpmCTaskAttributes1ApiEnum.OUTDATED) !== 0 &&
            (task.attributes1 & ECpmCTaskAttributes1ApiEnum.LAST_EXECUTION_FAILED) !== 0
        );
    }

    // ===============================================================
    // ===============================================================

    /**
     * Build a task tree from a list of task
     * @param tasks Task list
     * @returns Built task tree
     */
    public static buildTaskTree(
        tasks: TaskTreeViewExtension[],
        opts?: {
            /**
             * Decorate function
             * @param node Visited node
             */
            fnDecorate?: (node: TreeNode<TaskTreeViewExtension>) => void;
        }
    ): TreeNode<TaskTreeViewExtension>[] {
        return TreeUtils.flatToTreeNodes<TaskTreeViewExtension>(
            tasks,
            (x) => x.uuid,
            (x) => x.parent_uuid,
            {
                fnGetLabel: (x) => x.name,
                fnDecorate: opts?.fnDecorate
            }
        );
    }
    /**
     * Sort a task tree
     * @param taskTree Task tree to sort
     */
    public static sortTaskTree(taskTree: TreeNode<TaskTreeViewExtension>[]) {
        TreeUtils.sortTree(taskTree, (a, b) => (a.data!.order > b.data!.order ? 1 : -1));
    }
    /**
     * Add hierarchy numbers (recursive) as a prefix for all tasks
     * @param taskTree Tasks tree
     * @param addMode Where to add the prefix
     */
    public static addNumbersToTaskTree(taskTree: TreeNode<TaskTreeViewExtension>[], addMode: 'label' | 'name') {
        const labelPrefixPerKey: Record<string, string> = {};

        TreeUtils.visitTree(taskTree, (node) => {
            if (!node.key) return;
            if (node.key && node.key in labelPrefixPerKey) return;

            const index = node.parent ? node.parent.children?.indexOf(node) : taskTree.indexOf(node);
            labelPrefixPerKey[node.key] = (index! + 1).toString();

            if (node.parent?.key && node.parent.key in labelPrefixPerKey) {
                labelPrefixPerKey[node.key] = labelPrefixPerKey[node.parent.key] + '.' + labelPrefixPerKey[node.key];
            }

            if (addMode == 'name') node.data!.name = labelPrefixPerKey[node.key] + ' - ' + node.data?.name;
            else if (addMode == 'label') node.label = labelPrefixPerKey[node.key] + ' - ' + node.label;
        });
    }

    // ===============================================================
    // ===============================================================

    /**
     * Can we change a task status?
     * @param task Task instance
     * @returns True if the task status can be changed
     */
    public static canChangeTaskStatus(task?: CpmCTaskApiModel | TaskTreeViewExtension): boolean {
        if (!task) return false;

        if (task.task_type != ECpmCTaskTypeApiEnum.SIMPLE) return false;

        return true;
    }
    /**
     * Can we change a task status to?
     * @param task Task instance
     * @param status Target status
     * @returns True if the task status can be changed
     */
    public static canChangeTaskStatusTo(
        task: CpmCTaskApiModel | TaskTreeViewExtension | undefined,
        status: ECpmCTaskStatusApiEnum
    ): boolean {
        if (!task || !TaskUtils.canChangeTaskStatus(task)) return false;

        if (task.task_type != ECpmCTaskTypeApiEnum.SIMPLE) return false;

        switch (status) {
            case ECpmCTaskStatusApiEnum.READY:
                return task.status != ECpmCTaskStatusApiEnum.READY && task.status != ECpmCTaskStatusApiEnum.CREATED;

            case ECpmCTaskStatusApiEnum.IN_PROGRESS:
            case ECpmCTaskStatusApiEnum.SUCCESS:
            case ECpmCTaskStatusApiEnum.FAILED:
                return task.status != ECpmCTaskStatusApiEnum.CREATED;
        }

        return false;
    }
    /**
     * Can we execute a task?
     * @param task Task instance
     * @returns True if the task can be executed
     */
    public static canExecuteTask(task?: CpmCTaskApiModel | TaskTreeViewExtension): boolean {
        if (!task) return false;

        return [ECpmCTaskStatusApiEnum.READY, ECpmCTaskStatusApiEnum.SUCCESS, ECpmCTaskStatusApiEnum.FAILED].includes(
            task.status
        );
    }

    // ===============================================================
    // ===============================================================

    /**
     * Generate task source identifiers for reference expressions
     * @param sourceType Source type
     * @param asInput As input mapping?
     * @returns Generated list
     */
    public static async referenceExpressionSourceIdentifiersGenerator(
        sourceType: ECpmCReferenceExpressionSourceTypeApiEnum,
        asInput: boolean
    ): Promise<
        {
            label: string;
            value: string;
        }[]
    > {
        if (sourceType != ECpmCReferenceExpressionSourceTypeApiEnum.OBJECT_STORE) return [];

        const result = [
            {label: 'Customer Object Store', value: 'customer'},
            {label: "Organization Object Store (Relative to the Task's Service Request)", value: 'organization'},
            {label: "Project Object Store (Relative to the Task's Service Request)", value: 'project'},
            {label: 'Service Request (Relative to the Task)', value: 'service_request'}
        ];
        const lastItem = result.at(-1) ?? {label: '', value: ''};
        return asInput ? [lastItem] : result;
    }
    /**
     * Generate attributes name for task mapping reference expressions
     * @param sourceType Source type
     * @param objectStoreEntriesTransientCache Object store entries transient cache
     * @returns Generated list of tree node
     */
    public static async referenceExpressionAttributeNamesGenerator(
        sourceType: ECpmCReferenceExpressionSourceTypeApiEnum,
        objectStoreEntriesTransientCache: ObjectStoreEntriesTransientCache | undefined,
        objectStoreEntryVtype: ECpmCObjectStoreEntryProxyTypeApiEnum,
        objectStoreEntrySchemaKey: string | undefined
    ): Promise<TreeNode<string>[]> {
        if (!objectStoreEntriesTransientCache) return [];

        await objectStoreEntriesTransientCache.init();

        const definedRoot: TreeNode<string> = {
            label: StringUtils.toHumanFormatedStr(objectStoreEntriesTransientCache.lowestStoreLevel) + ' Object Store',
            selectable: false,
            expanded: true
        };
        const inheritedRoot: TreeNode<string> = {
            label: 'Inherited Object Store',
            selectable: false,
            expanded: true
        };

        definedRoot.children = objectStoreEntriesTransientCache.definedEntries
            .filter((x) => {
                // Hide internal value
                if ((x.attributes & ECpmCObjectStoreEntryAttributesProxyApiEnum.INTERNAL) != 0) return false;

                return true;
            })
            .map((x) => {
                const compatible = x.vtype == objectStoreEntryVtype && x.schema == objectStoreEntrySchemaKey;
                const result = {
                    key: x.key,
                    label: x.key,
                    data: x.key,
                    sourceIdentifier: objectStoreEntriesTransientCache.lowestStoreLevel,
                    typeLabel: StringUtils.toHumanFormatedStr(x.vtype),
                    selectable: compatible
                };

                return result;
            });
        definedRoot.children.sort((a, b) => {
            if (a.selectable && !b.selectable) return -1;
            else if (!a.selectable && b.selectable) return 1;

            return a.label! < b.label! ? -1 : 1;
        });

        inheritedRoot.children = objectStoreEntriesTransientCache.inheritedEntries
            .filter((x) => {
                // Hide internal value
                if ((x.attributes & ECpmCObjectStoreEntryAttributesProxyApiEnum.INTERNAL) != 0) return false;

                return true;
            })
            .map((x) => {
                const compatible = x.vtype == objectStoreEntryVtype && x.schema == objectStoreEntrySchemaKey;
                const entryLevel =
                    objectStoreEntriesTransientCache.getEntryLevel(x) ??
                    objectStoreEntriesTransientCache.lowestStoreLevel;
                const objectStoreLevelLabel = StringUtils.toHumanFormatedStr(entryLevel);

                const result = {
                    key: x.key,
                    label: x.key,
                    data: x.key,
                    levelLabel: objectStoreLevelLabel,
                    sourceIdentifier: entryLevel,
                    typeLabel: StringUtils.toHumanFormatedStr(x.vtype),
                    selectable: compatible
                };

                return result;
            });
        inheritedRoot.children.sort((a, b) => {
            if (a.selectable && !b.selectable) return -1;
            else if (!a.selectable && b.selectable) return 1;

            return a.label! < b.label! ? -1 : 1;
        });

        if (inheritedRoot.children.length > 0) return [definedRoot, inheritedRoot];
        else return [definedRoot];
    }

    // ===============================================================
    // ===============================================================

    /**
     * Check if a task is marked as a milestone
     * @param task Task instance
     * @returns True if the task is a SIMPLE task marked as milestone
     */
    public static isMilestone(task?: CpmCTaskApiModel | TaskTreeViewExtension): boolean {
        if (!task) return false;

        return (
            task.task_type === ECpmCTaskTypeApiEnum.SIMPLE &&
            (task.attributes1 & ECpmCTaskAttributes1ApiEnum.SIMPLE_IS_MILESTONE) !== 0
        );
    }

    /**
     * Check if a task status is executed (either SUCCESS or FAILED)
     * @param status Task status
     * @returns True if the task status is executed
     */
    public static isExecutedStatus(status?: ECpmCTaskStatusApiEnum): boolean {
        return status === ECpmCTaskStatusApiEnum.SUCCESS || status === ECpmCTaskStatusApiEnum.FAILED;
    }
}
